
import { Theme, ComparisonState } from './types';

export const THEME_BUTTON_CONFIG: Record<Theme, { bg: string, border: string, text: string, radius: string, font: string }> = {
  default: { bg: 'bg-indigo-600', border: 'border-indigo-400', text: 'text-white', radius: 'rounded-xl', font: 'font-sans' },
  material: { bg: 'bg-white', border: 'border-slate-200 shadow-sm', text: 'text-indigo-700', radius: 'rounded-full', font: 'font-sans' },
  neobrutalist: { bg: 'bg-[#ffde00]', border: 'border-black border-2 shadow-[4px_4px_0px_black]', text: 'text-black', radius: 'rounded-none', font: 'font-unbounded' },
  cyberpunk: { bg: 'bg-black', border: 'border-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.5)]', text: 'text-cyan-400', radius: 'rounded-none', font: 'font-mono-custom' },
  bauhaus: { bg: 'bg-[#E31E24]', border: 'border-slate-950 border-2', text: 'text-white', radius: 'rounded-none', font: 'font-grotesk' },
  bold: { bg: 'bg-black', border: 'border-white/40', text: 'text-white italic font-black', radius: 'rounded-none', font: 'font-unbounded' },
  flat: { bg: 'bg-[#2ecc71]', border: 'border-none', text: 'text-white', radius: 'rounded-none', font: 'font-sans' },
  glassmorphism: { bg: 'bg-white/10 backdrop-blur-md', border: 'border-white/20', text: 'text-white', radius: 'rounded-2xl', font: 'font-sans' },
  minimal: { bg: 'bg-white', border: 'border-slate-300', text: 'text-slate-900 font-light', radius: 'rounded-sm', font: 'font-grotesk' },
  dark_mode_2: { bg: 'bg-slate-950', border: 'border-slate-700 shadow-2xl ring-1 ring-white/5', text: 'text-slate-100', radius: 'rounded-2xl', font: 'font-sans' },
  dev: { bg: 'bg-[#0a0a0a]', border: 'border-[#00FF41]/30 hover:border-[#00FF41]', text: 'text-[#00FF41]', radius: 'rounded-none', font: 'font-mono-custom' },
  bento: { bg: 'bg-white', border: 'border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]', text: 'text-slate-800', radius: 'rounded-[2rem]', font: 'font-sans' },
  electric: { bg: 'bg-[#1a0033]', border: 'border-fuchsia-500 shadow-[0_0_15px_rgba(217,70,239,0.3)]', text: 'text-fuchsia-400', radius: 'rounded-xl', font: 'font-unbounded' },
  retro: { bg: 'bg-[#dcdcdc]', border: 'border-gray-500 border-b-4 border-r-4 border-t-2 border-l-2', text: 'text-[#000080]', radius: 'rounded-none', font: 'font-unbounded' },
  skeuomorphism: { bg: 'bg-gradient-to-b from-slate-100 to-slate-300', border: 'border-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8),0_4px_6px_-1px_rgba(0,0,0,0.1)]', text: 'text-slate-700', radius: 'rounded-2xl', font: 'font-sans' }
};

export const THEME_PRESETS: Partial<Record<Theme, Partial<ComparisonState>>> = {
  material: {
    theme: 'material',
    separatorColor: '#6200ee',
    accentColor: '#6200ee',
    fontFamily: 'Inter',
    separatorWidth: 2,
    bgColor: '#f5f5f5',
    borderRadius: 28,
    padding: 10,
    labelColor: '#1e293b',
    descColor: '#64748b'
  },
  neobrutalist: {
    theme: 'neobrutalist',
    separatorColor: '#ff0055',
    accentColor: '#ff0055',
    fontFamily: 'Unbounded',
    separatorWidth: 6,
    bgColor: '#ffde00',
    borderRadius: 0,
    padding: 10,
    labelColor: '#000000',
    descColor: '#000000'
  },
  cyberpunk: {
    theme: 'cyberpunk',
    separatorColor: '#00ff41',
    accentColor: '#f0f',
    fontFamily: 'Roboto Mono',
    separatorWidth: 2,
    bgColor: '#050505',
    borderRadius: 0,
    padding: 10,
    labelColor: '#22d3ee',
    descColor: '#f0abfc'
  },
  bauhaus: {
    theme: 'bauhaus',
    separatorColor: '#005596',
    accentColor: '#000000',
    fontFamily: 'Space Grotesk',
    separatorWidth: 8,
    bgColor: '#fdf9f3',
    borderRadius: 0,
    padding: 10,
    labelColor: '#000000',
    descColor: '#000000'
  },
  bold: {
    theme: 'bold',
    separatorColor: '#ffffff',
    accentColor: '#ffffff',
    fontFamily: 'Unbounded',
    separatorWidth: 4,
    bgColor: '#000000',
    borderRadius: 12,
    padding: 10,
    labelColor: '#ffffff',
    descColor: '#e2e8f0'
  },
  flat: {
    theme: 'flat',
    separatorColor: '#2ecc71',
    accentColor: '#2ecc71',
    fontFamily: 'Inter',
    separatorWidth: 2,
    bgColor: '#34495e',
    borderRadius: 0,
    padding: 10,
    labelColor: '#ffffff',
    descColor: '#ecf0f1'
  },
  glassmorphism: {
    theme: 'glassmorphism',
    separatorColor: 'rgba(255,255,255,0.5)',
    accentColor: '#ffffff',
    fontFamily: 'Inter',
    separatorWidth: 1,
    bgColor: '#1e3a8a',
    borderRadius: 40,
    padding: 20,
    labelColor: '#ffffff',
    descColor: 'rgba(255,255,255,0.8)'
  },
  minimal: {
    theme: 'minimal',
    separatorColor: '#111111',
    accentColor: '#000000',
    fontFamily: 'Space Grotesk',
    separatorWidth: 1,
    bgColor: '#ffffff',
    borderRadius: 4,
    padding: 40,
    labelColor: '#000000',
    descColor: '#334155'
  },
  dark_mode_2: {
    theme: 'dark_mode_2',
    accentColor: '#38bdf8',
    fontFamily: 'Inter',
    separatorWidth: 2,
    bgColor: '#020617',
    borderRadius: 32,
    padding: 16,
    separatorColor: '#1e293b',
    labelColor: '#f1f5f9',
    descColor: '#94a3b8'
  },
  dev: {
    theme: 'dev',
    accentColor: '#00FF41',
    fontFamily: 'Roboto Mono',
    separatorWidth: 1,
    bgColor: '#020202',
    borderRadius: 0,
    padding: 20,
    separatorColor: '#00FF41',
    labelColor: '#00FF41',
    descColor: '#008F11'
  },
  bento: {
    theme: 'bento',
    accentColor: '#1e293b',
    fontFamily: 'Inter',
    separatorWidth: 0,
    bgColor: '#f8fafc',
    borderRadius: 48,
    padding: 12,
    separatorColor: 'transparent',
    labelColor: '#0f172a',
    descColor: '#64748b'
  },
  electric: {
    theme: 'electric',
    accentColor: '#f0abfc',
    fontFamily: 'Unbounded',
    separatorWidth: 4,
    bgColor: '#0f0021',
    borderRadius: 24,
    padding: 10,
    separatorColor: '#bf00ff',
    labelColor: '#bf00ff',
    descColor: '#f0abfc'
  },
  retro: {
    theme: 'retro',
    accentColor: '#000080',
    fontFamily: 'Unbounded',
    separatorWidth: 6,
    bgColor: '#008080',
    borderRadius: 0,
    padding: 10,
    separatorColor: '#c0c0c0',
    labelColor: '#000000',
    descColor: '#000080'
  },
  skeuomorphism: {
    theme: 'skeuomorphism',
    accentColor: '#334155',
    fontFamily: 'Inter',
    separatorWidth: 3,
    bgColor: '#e2e8f0',
    borderRadius: 30,
    padding: 15,
    separatorColor: '#ffffff',
    labelColor: '#1e293b',
    descColor: '#475569'
  }
};
